import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MdOutlineClose, MdHelpOutline, MdCheckCircle, MdError } from 'react-icons/md';
import { sendMessage } from '../../Actions/user';
import InputWithLabel from '../InputCards/InputWithLabel';
import { trackFormSubmit } from '../../Actions/analytics';

const ContactCard = ({ onClose }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '', // We use this for UI, then merge into message
    message: ''
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFormValid = formData.name && formData.email && formData.subject && formData.message;

  const handleSubmit = async () => {
    if (!isFormValid || status === 'loading') return;

    setStatus('loading');

    // MAPPING TO YOUR DB: Merge Subject + Message into one "message" string
    const finalData = {
      name: formData.name,
      email: formData.email,
      message: `[TOPIC: ${formData.subject}] \n\n ${formData.message}`
    };

    try {
      await dispatch(sendMessage(finalData));
      trackFormSubmit('ask_a_senior');
      
      setStatus('success');
      setTimeout(() => onClose(), 5000);
    } catch (err) {
      setStatus('error');
      // Error handling: show error for 3 seconds then let them try again
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className='bg-white w-full max-w-lg mx-auto rounded-3xl overflow-hidden shadow-2xl border border-slate-100 relative'>
      
      {/* Header */}
      <div className={`p-6 text-white flex justify-between items-center transition-colors duration-500 ${status === 'success' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-blue-600'}`}>
        <div className='flex items-center gap-3'>
          <div className='bg-white/20 p-2 rounded-lg'>
            {status === 'success' ? <MdCheckCircle size={24}/> : status === 'error' ? <MdError size={24}/> : <MdHelpOutline size={24}/>}
          </div>
          <div>
            <h1 className='font-bold text-lg leading-tight'>
              {status === 'success' ? 'Message Sent!' : status === 'error' ? 'Failed to Send' : 'Ask a Senior'}
            </h1>
            <p className='text-white/80 text-xs'>
              {status === 'success' ? 'We will get back to you soon.' : 'Placement assistance desk'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className='hover:bg-white/10 p-1 rounded-full'><MdOutlineClose size={24}/></button>
      </div>

      <div className='p-8 space-y-4'>
        {/* Only show inputs if NOT success */}
        {status !== 'success' ? (
          <>
            {!user && (
              <div className='grid grid-cols-2 gap-4'>
                <InputWithLabel name="name" label="Name" link={formData.name} handleChange={handleChange} required />
                <InputWithLabel name="email" label="Email" link={formData.email} handleChange={handleChange} required />
              </div>
            )}

            <InputWithLabel 
               name="subject" 
               label="Subject / Topic" 
               link={formData.subject}
               handleChange={handleChange} 
               required 
            />

            <div>
              <h2 className='text-sm font-semibold text-gray-700 mb-2'>Detailed Question</h2>
              <textarea
                className="w-full h-32 p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
                placeholder="How can we help you?"
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={!isFormValid || status === 'loading'}
              className={`w-full py-4 rounded-xl font-bold transition-all flex justify-center items-center gap-2 ${
                isFormValid && status !== 'loading'
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md active:scale-95' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {status === 'loading' ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : status === 'error' ? (
                'Try Again'
              ) : (
                'Submit Query'
              )}
            </button>
          </>
        ) : (
          /* Success Message View */
          <div className='py-10 text-center space-y-4 animate-in zoom-in duration-300'>
             <div className='w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto'>
                <MdCheckCircle size={40}/>
             </div>
             <p className='font-medium text-slate-600'>Your query has been recorded. Check your email for a response!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactCard;